import { motion } from 'framer-motion'
import { MessageSquare, Loader2, Calendar, Flame, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useState, useMemo } from 'react'
import { useNostrTopics } from '../../hooks/useNostrTopics'
import { generateTopicUrl } from '../../lib/urlUtils'

type TabType = 'bugun' | 'populer' | 'yeni'

export function Sidebar() {
  const [activeTab, setActiveTab] = useState<TabType>('bugun')
  const { topics, isLoading } = useNostrTopics()

  // Filter or sort topics based on active tab
  const filteredTopics = useMemo(() => {
    const oneDayAgo = Math.floor(Date.now() / 1000) - 24 * 60 * 60
    const todayTopics = topics.filter(t => t.last_entry_at >= oneDayAgo)

    // Handle "Today" tab fallback
    if (activeTab === 'bugun') {
      if (todayTopics.length > 0) {
        return [...todayTopics].sort((a, b) => b.entry_count - a.entry_count).slice(0, 15)
      } else {
        // Empty Today? Show Popular instead of an empty list
        return [...topics].sort((a, b) => b.entry_count - a.entry_count).slice(0, 15)
      }
    }

    // Handle "New" tab
    if (activeTab === 'yeni') {
      return [...topics].sort((a, b) => b.last_entry_at - a.last_entry_at).slice(0, 15)
    }

    // Popular tab (default)
    return [...topics].sort((a, b) => b.entry_count - a.entry_count).slice(0, 15)
  }, [topics, activeTab])

  const isFallback = activeTab === 'bugun' && topics.length > 0 && topics.filter(t => t.last_entry_at >= Math.floor(Date.now() / 1000) - 24 * 60 * 60).length === 0

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: 'spring',
        damping: 20,
        stiffness: 150,
      },
    },
  }

  return (
    <div className="p-4">
      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-surface-100 dark:bg-surface-800/50 rounded-xl mb-6">
        {(['bugun', 'populer', 'yeni'] as TabType[]).map((tab) => {
          const Icon = tab === 'bugun' ? Calendar : tab === 'populer' ? Flame : Sparkles
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-3 text-[11px] font-poppins font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${activeTab === tab
                ? 'bg-white dark:bg-surface-700 shadow-sm text-mavi-600 dark:text-mavi-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {tab === 'bugun' ? 'Bugün' : tab === 'populer' ? 'Popüler' : 'Yeni'}
            </button>
          )
        })}
      </div>

      {isFallback && (
        <div className="mb-4 p-2 bg-mavi-50/50 dark:bg-mavi-900/10 border border-mavi-100/50 dark:border-mavi-900/20 rounded-lg">
          <p className="text-[10px] text-mavi-600 dark:text-mavi-400 font-poppins text-center leading-tight">
            Bugün henüz yeni entry girilmemiş. Popüler başlıkları görüyorsunuz.
          </p>
        </div>
      )}

      {isLoading && topics.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mb-2" />
          <p className="text-[11px] font-poppins text-center opacity-70">Relay'lere bağlanılıyor...</p>
        </div>
      ) : topics.length === 0 ? (
        <div className="text-center py-12 px-4 border border-dashed border-surface-200 dark:border-surface-800 rounded-lg">
          <p className="text-xs text-gray-500 dark:text-gray-400 font-poppins">
            Henüz aktif başlık bulunamadı.
          </p>
        </div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-1"
        >
          {filteredTopics.map((topic, index) => (
            <motion.div
              key={topic.id}
              variants={itemVariants}
            >
              <Link
                to={generateTopicUrl(topic.title, topic.id)}
                className="block p-3 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors group"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-poppins font-medium text-sm group-hover:text-mavi-600 dark:group-hover:text-mavi-400 transition-colors line-clamp-2">
                      {topic.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500 dark:text-gray-400 opacity-70 group-hover:opacity-100 transition-opacity">
                      <div className="flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        <span>{topic.entry_count} entry</span>
                      </div>
                    </div>
                  </div>
                  {activeTab === 'populer' && index < 3 && (
                    <span className="flex-shrink-0 w-6 h-6 bg-mavi-500/10 text-mavi-600 dark:text-mavi-400 text-[10px] font-poppins font-bold rounded-full flex items-center justify-center">
                      {index + 1}
                    </span>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  )
}
